import './hero-section.scss';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Split element text into word/char spans for masked reveal animations.
 *
 * @param {HTMLElement} el Element whose textContent to split
 */
function splitText( el ) {
	if ( ! el ) {
		return;
	}

	const text = el.textContent.trim();
	el.innerHTML = '';
	el.style.whiteSpace = 'normal';
	el.style.wordWrap = 'break-word';

	const words = text.split( ' ' );

	words.forEach( ( word, wordIdx ) => {
		const wordSpan = document.createElement( 'span' );
		wordSpan.className = 'val-word';
		wordSpan.style.display = 'inline-block';
		wordSpan.style.whiteSpace = 'nowrap';
		wordSpan.style.overflow = 'hidden';
		wordSpan.style.verticalAlign = 'top';
		wordSpan.style.lineHeight = '1.2';
		wordSpan.style.paddingBottom = '0.1em';
		wordSpan.style.marginBottom = '-0.1em';

		for ( const char of word ) {
			const charSpan = document.createElement( 'span' );
			charSpan.className = 'val-letter';
			charSpan.style.display = 'inline-block';
			charSpan.textContent = char;
			wordSpan.appendChild( charSpan );
		}

		el.appendChild( wordSpan );

		if ( wordIdx < words.length - 1 ) {
			el.appendChild( document.createTextNode( '\u00A0' ) );
		}
	} );
}

/**
 * Hero section — text reveal, parallax, video card intro.
 * Video playback is handled by the shared video-modal part via data-video-modal.
 */
function initHeroSection() {
	const root = document.querySelector( '[data-component="hero-section"]' );

	if ( ! root ) {
		return;
	}

	const heroTitle  = root.querySelector( '.title-centered-wrapper' );
	const heroBg     = root.querySelector( '.b-heroSection__bg' );
	const heroContent = root.querySelector( '.b-heroSection__content' );
	const videoCard  = root.querySelector( '[data-hero-video]' );
	const imgWrap    = root.querySelector( '[data-hero-img-wrap]' );
	const videoText  = root.querySelector( '[data-hero-video-text]' );
	const playBtn    = root.querySelector( '[data-hero-play-btn]' );

	const triggers = [];

	// ─── Text reveal animation ──────────────────────────────

	if ( heroTitle ) {
		const textElements = heroTitle.querySelectorAll( '.b-heroSection__revealText' );

		textElements.forEach( ( el, index ) => {
			splitText( el );

			const letters = el.querySelectorAll( '.val-letter' );

			gsap.set( letters, { yPercent: 110 } );
			gsap.set( el, { opacity: 1 } );

			gsap.to( letters, {
				yPercent: 0,
				duration: 1.2,
				stagger: 0.02,
				ease: 'power4.out',
				delay: index * 0.1,
			} );
		} );
	}

	// ─── Background zoom on scroll ──────────────────────────

	if ( heroBg ) {
		triggers.push(
			ScrollTrigger.create( {
				trigger: root,
				start: 'top top',
				end: 'bottom top',
				scrub: true,
				animation: gsap.to( heroBg, { scale: 1.15, ease: 'none' } ),
			} )
		);
	}

	// ─── Content parallax on scroll ─────────────────────────

	if ( heroContent ) {
		triggers.push(
			ScrollTrigger.create( {
				trigger: root,
				start: 'top top',
				end: 'bottom top',
				scrub: true,
				animation: gsap.to( heroContent, { yPercent: 50, ease: 'none' } ),
			} )
		);
	}

	// ─── Card image zoom on scroll ──────────────────────────

	if ( imgWrap ) {
		triggers.push(
			ScrollTrigger.create( {
				trigger: root,
				start: 'top top',
				end: 'bottom top',
				scrub: true,
				animation: gsap.to( imgWrap, { scale: 1.05, ease: 'none' } ),
			} )
		);
	}

	// ─── Video card intro animation ─────────────────────────

	if ( videoCard && imgWrap && videoText && playBtn ) {
		gsap.set( videoCard, { clipPath: 'inset(100% 0 0 0)' } );
		gsap.set( imgWrap, { clipPath: 'inset(100% 0 0 0)' } );
		gsap.set( videoText, { autoAlpha: 0, y: 15 } );
		gsap.set( playBtn, { scale: 0 } );

		const tl = gsap.timeline( {
			delay: 0.3,
			defaults: { ease: 'power3.inOut' },
		} );

		tl.to( videoCard, {
			clipPath: 'inset(0% 0 0 0)',
			autoAlpha: 1,
			duration: 0.8,
		} )
		.to( imgWrap, {
			clipPath: 'inset(0% 0 0 0)',
			duration: 0.8,
		}, '-=0.2' )
		.to( [ videoText, playBtn ], {
			autoAlpha: 1,
			y: 0,
			scale: 1,
			duration: 0.6,
			stagger: 0.1,
			ease: 'power2.out',
		}, '-=0.3' );
	}
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initHeroSection );
} else {
	initHeroSection();
}
